import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GraduationCap, LineChart, MessageSquareText } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { CheckboxRow, RadioCard, SelectField } from '@/components/ui/Field';
import { DatePicker } from '@/components/ui/DatePicker';
import { Icon } from '@/components/ui/Icon';
import { Stepper } from '@/components/ui/Navigation';
import { SkeletonCard } from '@/components/ui/States';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { BandValue } from '@/components/ui/BandScore';
import { filterPublicExams } from '@/lib/catalog-visibility';
import { authClient } from '@/lib/auth-client';
import { BRAND } from '@/lib/brand';
import { cn } from '@/lib/cn';
import { countryNames } from '@/lib/countries';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

const STEPS = [
  { label: 'Your exam' },
  { label: 'Your goal' },
  { label: 'Exam date' },
  { label: 'Where you are' },
];

const LAST_QUESTION = STEPS.length;

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'Pacific/Auckland';
  }
}

function todayLocal(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${String(now.getFullYear())}-${month}-${day}`;
}

const TODAY = todayLocal();
const COUNTRIES = countryNames();

/** What a student gets, shown on the welcome screen. */
const WELCOME_POINTS = [
  {
    icon: GraduationCap,
    title: 'Exam-style practice',
    text: 'Listening, reading, writing and speaking, in the current IELTS format.',
  },
  {
    icon: MessageSquareText,
    title: 'Feedback on every answer',
    text: 'See what went wrong and how to fix it.',
  },
  {
    icon: LineChart,
    title: 'Estimate your band',
    text: 'Band estimates and progress towards your target.',
  },
] as const;

const SCREEN_TEXT: Record<number, { title: string; description: string }> = {
  1: {
    title: 'Which exam are you sitting?',
    description: 'We use this to pick your practice and your marking criteria.',
  },
  2: {
    title: 'What band do you need?',
    description: 'We show this as a target line on your progress.',
  },
  3: {
    title: 'When is your exam?',
    description: 'We count the days for you. You can change this later.',
  },
  4: {
    title: 'Where are you?',
    description: 'Tell us your country so we can tailor your practice.',
  },
  5: {
    title: 'Check where you are now',
    description: 'A short test across all four skills shows your level and where to start.',
  },
};

/** Band targets, in the 0.5 steps the scale uses. */
const TARGETS = [5.5, 6.0, 6.5, 7.0, 7.5, 8.0];

function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = authClient.useSession();

  // 0 = welcome, 1 = exam, 2 = target band, 3 = exam date, 4 = country, 5 = diagnostic offer
  const [step, setStep] = useState(0);
  const [variantSlug, setVariantSlug] = useState<string | null>(null);
  const [target, setTarget] = useState<number | null>(null);
  const [notBooked, setNotBooked] = useState(false);
  const [examDate, setExamDate] = useState('');
  const [country, setCountry] = useState('');
  // The time zone is read from the browser; students are not asked for it.
  const [timezone] = useState(browserTimeZone);

  const examsQuery = useQuery({
    queryKey: ['exams'],
    queryFn: () => api.get('/api/v1/exams'),
  });

  const save = useMutation({
    mutationFn: () =>
      api.put('/api/v1/me/profile', {
        body: {
          targetExamVariantSlug: variantSlug,
          targetScore: target,
          examDate: notBooked || examDate === '' ? null : examDate,
          country: country.trim() === '' ? null : country.trim(),
          timezone,
        },
      }),
    onSuccess: async () => {
      // The dashboard still holds the old "not onboarded" profile and would send the student straight
      // back here. Refetch before leaving this page.
      await queryClient.refetchQueries({ queryKey: ['me-profile'] });
      setStep(LAST_QUESTION + 1);
    },
  });

  if (!session) {
    return (
      <AuthLayout title="Log in first" description="We save your goal to your account.">
        <Button onClick={() => void navigate({ to: '/sign-in' })} fullWidth size="lg">
          Log in
        </Button>
      </AuthLayout>
    );
  }

  const variants = filterPublicExams(examsQuery.data?.exams ?? []).flatMap((exam) =>
    exam.variants.map((variant) => ({
      slug: variant.slug,
      title: exam.name === variant.name ? variant.name : `${exam.name} · ${variant.name}`,
      description: variant.name.toLowerCase().includes('general')
        ? 'For work, training and migration.'
        : 'For university and professional registration.',
    })),
  );

  if (step === 0) {
    const firstName = session.user.name?.trim().split(/\s+/)[0];
    return (
      <AuthLayout
        title={firstName ? `Welcome, ${firstName}` : 'Welcome'}
        description={`Let's set up your practice with ${BRAND.productShort}. It takes about a minute.`}
      >
        <div className="grid gap-5">
          <ul className="grid gap-3">
            {WELCOME_POINTS.map((point) => (
              <li key={point.title} className="flex items-start gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-primary-tint text-primary">
                  <Icon icon={point.icon} size={20} />
                </span>
                <span className="min-w-0">
                  <span className="block text-body font-bold text-ink">{point.title}</span>
                  <span className="block text-small text-ink-2">{point.text}</span>
                </span>
              </li>
            ))}
          </ul>
          <Button
            size="lg"
            fullWidth
            onClick={() => {
              setStep(1);
            }}
          >
            Continue
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={SCREEN_TEXT[step]?.title ?? ''}
      description={SCREEN_TEXT[step]?.description ?? ''}
    >
      <div className="grid gap-6">
        {step <= LAST_QUESTION ? <Stepper steps={STEPS} current={step} /> : null}

        {save.isError ? (
          <Alert tone="danger" title="We could not save that.">
            Check your connection and try again.
          </Alert>
        ) : null}

        {step === 1 ? (
          examsQuery.isPending ? (
            <SkeletonCard />
          ) : (
            <>
              <div className="grid gap-2" role="radiogroup" aria-label="Exam">
                {variants.map((variant) => (
                  <RadioCard
                    key={variant.slug}
                    title={variant.title}
                    description={variant.description}
                    checked={variantSlug === variant.slug}
                    onSelect={() => {
                      setVariantSlug(variant.slug);
                    }}
                  />
                ))}
              </div>
              <Button
                size="lg"
                fullWidth
                disabled={variantSlug === null}
                onClick={() => {
                  setStep(2);
                }}
              >
                Continue
              </Button>
            </>
          )
        ) : step === 2 ? (
          <>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Target band">
              {TARGETS.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={target === value}
                  onClick={() => {
                    setTarget(value);
                  }}
                  className={cn(
                    'focus-ring rounded-[12px] border-[1.5px] p-1',
                    target === value ? 'border-primary bg-primary-tint' : 'border-border',
                  )}
                >
                  <BandValue value={value} size="sm" target={target !== value} />
                </button>
              ))}
            </div>

            <div className="flex gap-2.5">
              <Button
                variant="secondary"
                size="lg"
                className="shrink-0"
                onClick={() => {
                  setStep(1);
                }}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="flex-1"
                disabled={target === null}
                onClick={() => {
                  setStep(3);
                }}
              >
                Continue
              </Button>
            </div>
          </>
        ) : step === 3 ? (
          <>
            <DatePicker
              label="Exam date"
              min={TODAY}
              value={examDate}
              disabled={notBooked}
              onChange={setExamDate}
            />
            <CheckboxRow
              label="I have not booked yet"
              checked={notBooked}
              onToggle={() => {
                setNotBooked((value) => !value);
              }}
            />
            <div className="flex gap-2.5">
              <Button
                variant="secondary"
                size="lg"
                className="shrink-0"
                onClick={() => {
                  setStep(2);
                }}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="flex-1"
                disabled={!notBooked && examDate === ''}
                onClick={() => {
                  setStep(4);
                }}
              >
                Continue
              </Button>
            </div>
          </>
        ) : step === 4 ? (
          <>
            <SelectField
              label="Country"
              value={country}
              autoComplete="country-name"
              onChange={(event) => {
                setCountry(event.target.value);
              }}
            >
              <option value="">Choose your country</option>
              {COUNTRIES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </SelectField>
            <div className="flex gap-2.5">
              <Button
                variant="secondary"
                size="lg"
                className="shrink-0"
                onClick={() => {
                  setStep(3);
                }}
              >
                Back
              </Button>
              <Button
                size="lg"
                className="flex-1"
                loading={save.isPending}
                onClick={() => {
                  save.mutate();
                }}
              >
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-body text-ink-2">
              About 35 minutes: some Listening and Reading questions, one short Writing task and one
              Speaking part. You can skip it and take it later from your dashboard.
            </p>
            <Button
              size="lg"
              fullWidth
              onClick={() => {
                void navigate({ to: '/diagnostic' });
              }}
            >
              Take the diagnostic
            </Button>
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              onClick={() => {
                void navigate({ to: '/dashboard' });
              }}
            >
              Skip for now
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
